/**
 * OAuthCallbackHandler — handles OAuth redirect callbacks.
 *
 * This component mounts in the AppShell and checks on load whether the
 * current URL contains OAuth callback parameters (code, state). If so,
 * it processes the callback by exchanging the authorization code for tokens
 * via oauthService.handleCallback(), then cleans up the URL and refreshes
 * calendar connections.
 *
 * Since the app uses state-driven routing (not URL-based), the OAuth redirect
 * comes back to the app's root URL with query params. This component detects
 * those params, handles the exchange, and removes them from the URL.
 *
 * @see Requirements 6.1, 6.2, 17.1, 17.5
 */

import { useEffect, useState, useRef } from 'react';
import { oauthService, retrieveOAuthProvider } from '../../services/oauthService';
import { useCalendarStore } from '../../stores';
import { useUIStore } from '../../stores/uiStore';
import type { CalendarProvider } from '../../types/calendar';

/** Valid calendar providers. */
const VALID_PROVIDERS: CalendarProvider[] = ['google', 'microsoft', 'apple'];

function isValidProvider(value: string | null): value is CalendarProvider {
  return value !== null && VALID_PROVIDERS.includes(value as CalendarProvider);
}

export function OAuthCallbackHandler() {
  const [status, setStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successProvider, setSuccessProvider] = useState<string | null>(null);
  const processedRef = useRef(false);

  const loadConnections = useCalendarStore((s) => s.loadConnections);
  const setActiveModule = useUIStore((s) => s.setActiveModule);

  useEffect(() => {
    // Only process once
    if (processedRef.current) return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');

    // If no OAuth callback params, nothing to do
    if (!code || !state) return;

    processedRef.current = true;
    setStatus('processing');

    async function processCallback() {
      try {
        // Retrieve the provider stored before the redirect
        const provider = retrieveOAuthProvider();

        if (!isValidProvider(provider)) {
          throw new Error(
            'Could not determine calendar provider. The session may have expired. Please try connecting again.'
          );
        }

        // Exchange the authorization code for tokens
        const result = await oauthService.handleCallback(code!, state!, provider);

        // Clean up the URL (remove OAuth params without a page reload)
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);

        // Refresh connections list in the calendar store
        await loadConnections();

        setSuccessProvider(result.providerEmail || provider);
        setStatus('success');

        // Navigate to the Calendar module to show the new connection
        setActiveModule('calendar');

        // Auto-dismiss success message after 5 seconds
        setTimeout(() => {
          setStatus('idle');
          setSuccessProvider(null);
        }, 5000);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to complete calendar connection.';
        setErrorMessage(message);
        setStatus('error');

        // Clean up the URL even on error
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);

        // Auto-dismiss error after 8 seconds
        setTimeout(() => {
          setStatus('idle');
          setErrorMessage(null);
        }, 8000);
      }
    }

    void processCallback();
  }, [loadConnections, setActiveModule]);

  // Don't render anything when idle
  if (status === 'idle') return null;

  // Processing state
  if (status === 'processing') {
    return (
      <div
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-lg border border-border bg-bg-card px-4 py-3 shadow-lg"
        role="status"
        aria-live="polite"
      >
        <div className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin" aria-hidden="true" />
        <span className="text-sm text-text-primary">Connecting calendar...</span>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-lg border border-success/30 bg-success/10 px-4 py-3 shadow-lg"
        role="status"
        aria-live="polite"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-success"
          aria-hidden="true"
        >
          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-sm text-success">
          Calendar connected successfully{successProvider ? ` (${successProvider})` : ''}
        </span>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-lg border border-error/30 bg-error/10 px-4 py-3 shadow-lg max-w-md"
        role="alert"
        aria-live="assertive"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-error flex-shrink-0"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span className="text-sm text-error">{errorMessage}</span>
      </div>
    );
  }

  return null;
}

OAuthCallbackHandler.displayName = 'OAuthCallbackHandler';
